/**
 * THE HELP SOCKET PROTOCOL — plan-019 DOM (plan-019 §2.10, design §31.5 which wins where they differ; ADR 0065,
 * proposed; glossary §6.33 "Help socket"). Contract IC-H1: HOST-A and HOST-B speak it on the server, WID-CORE and
 * WID-FEAT in the widget, LOAD and E2E drive it.
 *
 * `wss://<runtime public host>/v1/help/socket`. Text frames of JSON, every frame an object with a `type`. The first
 * client frame is `hello` within {@link HELP_HELLO_TIMEOUT_MS}; the widget beats every {@link HELP_BEAT_INTERVAL_MS};
 * {@link HELP_BEAT_DETACH_MS} without a beat detaches (the conversation stays open until the idle sweep).
 *
 * The guards are tiny and hand-written — no zod — so the widget's launcher stays inside its 25 KiB budget
 * (design §31.8.3). This module imports nothing outside `packages/domain/src/help`, and the modules it imports import
 * nothing at all (`protocol.test.ts` proves it; `page-snapshot` joined them 2026-10-18). The server validates with the same functions; the frame byte limit
 * (`WETALK_RUNTIME_HELP_FRAME_BYTE_LIMIT`) is configuration and is checked by the help host before parsing.
 *
 * Every guard is exact: a frame with a field it does not know is refused, as is a field of the wrong shape. Wire names
 * are `snake_case`, like every wetalk JSON body.
 */
import { HELP_ACTION_REFUSAL_REASON } from "./action.js";
import { HELP_ACCOUNT_ACTION, HELP_ACCOUNT_ACTION_FIELD, isHelpAccountActionCode, } from "./account-action.js";
import { HELP_ANSWER_LINK_LIMIT, HELP_DISPLAY_NAME_CHAR_LIMIT, HELP_HISTORY_TURN_LIMIT, HELP_MESSAGE_CHAR_LIMIT, HELP_OFFER_SUMMARY_CHAR_LIMIT, HELP_OPERATOR_LINE_CHAR_LIMIT, HELP_PAGE_CONTEXT_BYTE_LIMIT, HELP_PROPOSAL_FIELD_LIMIT, } from "./limit.js";
import { HELP_PAGE_SNAPSHOT_BYTE_LIMIT, isHelpPageSnapshot } from "./page-snapshot.js";
import { isHelpRequestKind } from "./request-kind.js";
import { isHelpLanguage, isHelpSurface } from "./surface.js";
/** The protocol's number, sent in `hello` and `ready`. A change of shape is a new number, never a silent edit. */
export const HELP_PROTOCOL = 1;
/** The first frame must arrive within this many milliseconds, else close 1008. */
export const HELP_HELLO_TIMEOUT_MS = 5_000;
/** The widget sends `beat` this often while attached. */
export const HELP_BEAT_INTERVAL_MS = 10_000;
/** This long without a beat detaches the socket from its conversation. */
export const HELP_BEAT_DETACH_MS = 30_000;
/** After `closed {moved}`: the wait before each reconnect — one at once, then 1 s, 2 s, 4 s, then give up (design §31.5.4). */
export const HELP_RECONNECT_BACKOFF_MS = [0, 1_000, 2_000, 4_000];
/** The widget debounces `page_context` by this much. */
export const HELP_PAGE_CONTEXT_DEBOUNCE_MS = 300;
/** WebSocket close codes the help host uses (RFC 6455). */
export const HELP_SOCKET_CLOSE_CODE = Object.freeze({
    /** After a `closed` frame. */
    normal: 1000,
    /** No `hello` in time, malformed JSON, an unknown `type`, a frame of the wrong shape, a second `hello`. */
    policy_violation: 1008,
    /** A frame over the configured byte limit, or a `page_context` over {@link HELP_PAGE_CONTEXT_BYTE_LIMIT}. */
    message_too_big: 1009,
});
// ── shared value shapes ─────────────────────────────────────────────────────────────────────
/** `assistant` while the help assistant answers; `operator` while an operator holds the conversation. */
export const HELP_CONVERSATION_MODE = ["assistant", "operator"];
/** The speakers a person sees in the replayed history (never `system`). */
export const HELP_HISTORY_SPEAKER = ["caller", "assistant", "operator"];
/** How deep a {@link HelpFillObject} may nest (an opening-hours value: object → list → object → list → text). */
export const HELP_FILL_OBJECT_DEPTH = 4;
/** At most this many options per reported field. */
export const HELP_PAGE_OPTION_LIMIT = 32;
/** At most this many places (a wizard's steps, a page's tabs) and this many state entries in one `page_context`. */
export const HELP_PAGE_PLACE_LIMIT = 16;
export const HELP_PAGE_STATE_LIMIT = 16;
export const HELP_CLIENT_FRAME_TYPE = [
    "hello",
    "message",
    "page_context",
    "page_snapshot",
    "language",
    "proposal_outcome",
    "ask_for_person",
    "beat",
    "bye",
    "account_action_outcome",
];
export const HELP_NO_PERSON_REASON = ["none_available", "no_join"];
/** Why the mode changed; the widget renders the join, back and left lines from it in the page language. */
export const HELP_MODE_CAUSE = ["takeover", "hand_back", "lapsed", "superseded"];
/**
 * Why a message got no model answer. `operator_holds` is informational: the message reached the operator only, and the
 * widget shows nothing extra.
 */
export const HELP_REFUSED_REASON = [
    "message_too_long",
    "turn_limit",
    "rate_limited",
    "budget_reached",
    "busy",
    "open_limit",
    "operator_holds",
];
/**
 * Why the server closes: `moved` (drain or restart — reconnect once with the resume path), `replaced` (another tab
 * attached — do not reconnect), `scope_changed` (the portal asks a new grant), the rest end the attempt.
 */
export const HELP_CLOSED_REASON = [
    "idle",
    "moved",
    "replaced",
    "turn_limit",
    "ended",
    "grant_invalid",
    "help_off",
    "scope_changed",
];
export const HELP_SERVER_FRAME_TYPE = [
    "ready",
    "reply_start",
    "reply_text",
    "reply_end",
    "proposal",
    "offer",
    "waiting",
    "no_person",
    "mode",
    "operator_line",
    "refused",
    "scrubbed",
    "page_reading_offer",
    "closed",
    "account_action",
    "account_read_note",
];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** 16 random bytes, base64url, unpadded. */
const TOKEN_22 = /^[A-Za-z0-9_-]{22}$/;
/** 0153's `help_request.route_id` and `help_action.route_id` CHECK. */
const ROUTE_ID = /^[a-z][a-z0-9_.]{0,79}$/;
/** 0153's `help_action.form_id` CHECK; field codes take the same shape. */
const FORM_ID = /^[a-z][a-z0-9_]{0,63}$/;
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:\d{2})$/;
function fieldsOf(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? value : null;
}
/** Every own key is one of `allowed`, and every key of `required` is present (not `undefined`). */
function exactKeys(fields, required, optional = []) {
    for (const key of Object.keys(fields)) {
        if (!required.includes(key) && !optional.includes(key))
            return false;
        if (fields[key] === undefined)
            return false;
    }
    return required.every((key) => key in fields);
}
/** Characters as a person counts them (code points, so an emoji is one). */
function charCountOf(text) {
    return Array.from(text).length;
}
function isText(value, min, max) {
    if (typeof value !== "string")
        return false;
    const count = charCountOf(value);
    return count >= min && count <= max;
}
function isUuid(value) {
    return typeof value === "string" && UUID.test(value);
}
function isToken22(value) {
    return typeof value === "string" && TOKEN_22.test(value);
}
function isRouteId(value) {
    return typeof value === "string" && ROUTE_ID.test(value);
}
function isCode(value) {
    return typeof value === "string" && FORM_ID.test(value);
}
function isPagePath(value) {
    return isText(value, 1, 512) && value.startsWith("/") && !value.startsWith("//");
}
function isIndex(value) {
    return typeof value === "number" && Number.isInteger(value) && value >= 0;
}
function isInstant(value) {
    return typeof value === "string" && ISO_INSTANT.test(value) && !Number.isNaN(Date.parse(value));
}
function isOneOf(value, set) {
    return typeof value === "string" && set.includes(value);
}
const FILL_OBJECT_KEY = /^[a-z0-9_]{1,32}$/;
/** A list inside a fill object holds at most this many entries (an opening-hours value: 60 special dates). */
const FILL_OBJECT_LIST_LIMIT = 64;
function isFillJson(value, depth) {
    if (typeof value === "string" || typeof value === "boolean")
        return true;
    if (typeof value === "number")
        return Number.isFinite(value);
    if (depth >= HELP_FILL_OBJECT_DEPTH)
        return false;
    if (Array.isArray(value))
        return value.length <= FILL_OBJECT_LIST_LIMIT && value.every((one) => isFillJson(one, depth + 1));
    const f = fieldsOf(value);
    if (f === null)
        return false;
    const key = Object.keys(f);
    return key.length <= FILL_OBJECT_LIST_LIMIT && key.every((one) => FILL_OBJECT_KEY.test(one) && isFillJson(f[one], depth + 1));
}
/** A JSON scalar, a flat array of strings and finite numbers, or a bounded fill object (2026-10-18). */
export function isHelpFillValue(value) {
    if (typeof value === "string" || typeof value === "boolean")
        return true;
    if (typeof value === "number")
        return Number.isFinite(value);
    if (Array.isArray(value))
        return value.every((item) => typeof item === "string" || (typeof item === "number" && Number.isFinite(item)));
    return fieldsOf(value) !== null && isFillJson(value, 0);
}
/** A link the widget may render: a same-site path or an `https:` URL. */
export function isHelpLinkHref(value) {
    if (typeof value !== "string" || value.length === 0 || value.length > 2_048)
        return false;
    for (let i = 0; i < value.length; i += 1) {
        const unit = value.charCodeAt(i);
        // A control character (C0 and DEL) or whitespace never belongs in a link.
        if (unit <= 0x20 || unit === 0x7f)
            return false;
    }
    if (/\s/.test(value))
        return false;
    if (value.startsWith("/"))
        return !value.startsWith("//") && !value.startsWith("/\\");
    return /^https:\/\/[^/?#\\]+(?:[/?#]|$)/i.test(value);
}
/** The trimmed text of a person's message, or `null` when it is empty or longer than {@link HELP_MESSAGE_CHAR_LIMIT}. */
export function helpMessageTextOf(text) {
    const trimmed = text.trim();
    const count = charCountOf(trimmed);
    return count >= 1 && count <= HELP_MESSAGE_CHAR_LIMIT ? trimmed : null;
}
/** UTF-8 bytes of a string, without an encoder (the widget and Node agree). */
export function utf8ByteLengthOf(text) {
    let bytes = 0;
    for (const char of text) {
        const point = char.codePointAt(0) ?? 0;
        bytes += point < 0x80 ? 1 : point < 0x800 ? 2 : point < 0x10000 ? 3 : 4;
    }
    return bytes;
}
const PAGE_REF_KEY = ["route_id", "screen_label", "page_path"];
function isPageRefPart(fields) {
    if ("route_id" in fields && !isRouteId(fields["route_id"]))
        return false;
    if ("screen_label" in fields && !isText(fields["screen_label"], 1, 120))
        return false;
    if ("page_path" in fields && !isPagePath(fields["page_path"]))
        return false;
    return true;
}
// ── client frame guards ─────────────────────────────────────────────────────────────────────
function isHello(f) {
    if (!exactKeys(f, ["type", "protocol", "language", "surface", "page", "widget_build"], ["grant", "resume_token"]))
        return false;
    if (f["protocol"] !== HELP_PROTOCOL)
        return false;
    const hasGrant = "grant" in f;
    if (hasGrant === "resume_token" in f)
        return false;
    if (hasGrant ? !isToken22(f["grant"]) : !isToken22(f["resume_token"]))
        return false;
    if (!isHelpLanguage(f["language"]) || !isHelpSurface(f["surface"]))
        return false;
    if (!isText(f["widget_build"], 1, 80))
        return false;
    const page = fieldsOf(f["page"]);
    return page !== null && exactKeys(page, [], PAGE_REF_KEY) && isPageRefPart(page);
}
function isMessage(f) {
    return (exactKeys(f, ["type", "client_message_id", "text"]) &&
        isUuid(f["client_message_id"]) &&
        typeof f["text"] === "string" &&
        helpMessageTextOf(f["text"]) !== null);
}
function isPageContextOption(value) {
    const f = fieldsOf(value);
    return f !== null && exactKeys(f, ["value", "label"]) && isText(f["value"], 1, 64) && isText(f["label"], 1, 120);
}
function isPageContextField(value) {
    const f = fieldsOf(value);
    if (f === null || !exactKeys(f, ["code", "kind"], ["value", "label", "option", "filled"]))
        return false;
    if ("label" in f && !isText(f["label"], 1, 120))
        return false;
    const option = f["option"];
    if ("option" in f && !(Array.isArray(option) && option.length <= HELP_PAGE_OPTION_LIMIT && option.every(isPageContextOption)))
        return false;
    if ("filled" in f && typeof f["filled"] !== "boolean")
        return false;
    return isCode(f["code"]) && isCode(f["kind"]) && (!("value" in f) || isHelpFillValue(f["value"]));
}
function isPageContextForm(value) {
    const f = fieldsOf(value);
    if (f === null || !exactKeys(f, ["form_id", "field"]))
        return false;
    return isCode(f["form_id"]) && Array.isArray(f["field"]) && f["field"].every(isPageContextField);
}
function isPageContextState(value) {
    const f = fieldsOf(value);
    return f !== null && exactKeys(f, ["code", "filled"]) && isCode(f["code"]) && typeof f["filled"] === "boolean";
}
function isPagePlacePart(f) {
    if ("place" in f && !isCode(f["place"]))
        return false;
    const offered = f["place_list"];
    if ("place_list" in f && !(Array.isArray(offered) && offered.length <= HELP_PAGE_PLACE_LIMIT && offered.every(isCode)))
        return false;
    const state = f["state"];
    if ("state" in f && !(Array.isArray(state) && state.length <= HELP_PAGE_STATE_LIMIT && state.every(isPageContextState)))
        return false;
    return true;
}
function isPageContext(f) {
    if (!exactKeys(f, ["type", "workspace_open", "form"], [...PAGE_REF_KEY, "place", "place_list", "state"]))
        return false;
    return (isPageRefPart(f) &&
        isPagePlacePart(f) &&
        typeof f["workspace_open"] === "boolean" &&
        Array.isArray(f["form"]) &&
        f["form"].every(isPageContextForm));
}
function isProposalOutcome(f) {
    if (!exactKeys(f, ["type", "proposal_id", "outcome"], ["reason"]))
        return false;
    if (!isUuid(f["proposal_id"]))
        return false;
    if (!isOneOf(f["outcome"], ["accepted", "dismissed", "refused"]))
        return false;
    // 0153's help_action_refusal_shape: a reason exactly when refused.
    return f["outcome"] === "refused" ? isOneOf(f["reason"], HELP_ACTION_REFUSAL_REASON) : !("reason" in f);
}
function isAccountActionOutcome(f) {
    return (exactKeys(f, ["type", "help_action_id", "outcome"]) &&
        isUuid(f["help_action_id"]) &&
        isOneOf(f["outcome"], ["confirmed", "dismissed", "expired"]));
}
const CLIENT_GUARD = {
    hello: isHello,
    message: isMessage,
    page_context: isPageContext,
    page_snapshot: (f) => exactKeys(f, ["type", "snapshot"]) && isHelpPageSnapshot(f["snapshot"]),
    language: (f) => exactKeys(f, ["type", "code"]) && isHelpLanguage(f["code"]),
    proposal_outcome: isProposalOutcome,
    ask_for_person: (f) => exactKeys(f, ["type"]),
    beat: (f) => exactKeys(f, ["type"]),
    bye: (f) => exactKeys(f, ["type"]),
    account_action_outcome: isAccountActionOutcome,
};
/** Whether a parsed value is exactly one of the client frames. */
export function isHelpClientFrame(value) {
    const f = fieldsOf(value);
    if (f === null || !isOneOf(f["type"], HELP_CLIENT_FRAME_TYPE))
        return false;
    return CLIENT_GUARD[f["type"]](f);
}
/** Why a client frame was refused; each closes the socket with {@link helpCloseCodeOf}'s code. */
export const HELP_FRAME_REFUSAL = ["malformed", "unknown_type", "invalid", "too_large"];
/**
 * Reads one text frame from the widget. `malformed`: not JSON, or not an object with a string `type`; `unknown_type`;
 * `invalid`: a known type of the wrong shape; `too_large`: a `page_context` over {@link HELP_PAGE_CONTEXT_BYTE_LIMIT}
 * bytes. Never throws.
 */
export function readHelpClientFrame(text) {
    let value;
    try {
        value = JSON.parse(text);
    }
    catch {
        return { ok: false, refusal: "malformed" };
    }
    const f = fieldsOf(value);
    if (f === null || typeof f["type"] !== "string")
        return { ok: false, refusal: "malformed" };
    if (!isOneOf(f["type"], HELP_CLIENT_FRAME_TYPE))
        return { ok: false, refusal: "unknown_type" };
    if (f["type"] === "page_context" && utf8ByteLengthOf(text) > HELP_PAGE_CONTEXT_BYTE_LIMIT) {
        return { ok: false, refusal: "too_large" };
    }
    if (f["type"] === "page_snapshot" && utf8ByteLengthOf(text) > HELP_PAGE_SNAPSHOT_BYTE_LIMIT) {
        return { ok: false, refusal: "too_large" };
    }
    return CLIENT_GUARD[f["type"]](f) ? { ok: true, frame: f } : { ok: false, refusal: "invalid" };
}
/** The close code for a refused frame. */
export function helpCloseCodeOf(refusal) {
    return refusal === "too_large" ? HELP_SOCKET_CLOSE_CODE.message_too_big : HELP_SOCKET_CLOSE_CODE.policy_violation;
}
// ── server frame guards ─────────────────────────────────────────────────────────────────────
function isHistoryTurn(value) {
    const f = fieldsOf(value);
    return (f !== null &&
        exactKeys(f, ["turn_index", "speaker", "text"]) &&
        isIndex(f["turn_index"]) &&
        isOneOf(f["speaker"], HELP_HISTORY_SPEAKER) &&
        typeof f["text"] === "string");
}
function isReady(f) {
    if (!exactKeys(f, ["type", "protocol", "conversation_id", "mode", "notice_revision", "history", "message_char_limit"], ["resume_token", "display_name", "waiting_since"])) {
        return false;
    }
    if (f["protocol"] !== HELP_PROTOCOL || !isUuid(f["conversation_id"]))
        return false;
    if ("resume_token" in f && !isToken22(f["resume_token"]))
        return false;
    if (!isOneOf(f["mode"], HELP_CONVERSATION_MODE))
        return false;
    // The holder's name exactly while an operator holds the conversation.
    if (f["mode"] === "operator" ? !isText(f["display_name"], 1, HELP_DISPLAY_NAME_CHAR_LIMIT) : "display_name" in f)
        return false;
    if (!isText(f["notice_revision"], 1, 64))
        return false;
    if ("waiting_since" in f && !isInstant(f["waiting_since"]))
        return false;
    const history = f["history"];
    if (!Array.isArray(history) || history.length > HELP_HISTORY_TURN_LIMIT || !history.every(isHistoryTurn))
        return false;
    const limit = f["message_char_limit"];
    return typeof limit === "number" && Number.isInteger(limit) && limit >= 1;
}
function isLink(value) {
    const f = fieldsOf(value);
    return f !== null && exactKeys(f, ["title", "href"]) && isText(f["title"], 1, 200) && isHelpLinkHref(f["href"]);
}
function isReplyEnd(f) {
    if (!exactKeys(f, ["type", "turn_index", "link", "cut"]))
        return false;
    const link = f["link"];
    return (isIndex(f["turn_index"]) &&
        Array.isArray(link) &&
        link.length <= HELP_ANSWER_LINK_LIMIT &&
        link.every(isLink) &&
        typeof f["cut"] === "boolean");
}
function isProposedField(value) {
    const f = fieldsOf(value);
    return f !== null && exactKeys(f, ["code", "value"]) && isCode(f["code"]) && isHelpFillValue(f["value"]);
}
function isProposal(f) {
    if (f["kind"] === "navigate") {
        return exactKeys(f, ["type", "proposal_id", "kind", "route_id"]) && isUuid(f["proposal_id"]) && isRouteId(f["route_id"]);
    }
    if (f["kind"] !== "fill_form" || !exactKeys(f, ["type", "proposal_id", "kind", "form_id", "field"]))
        return false;
    const field = f["field"];
    return (isUuid(f["proposal_id"]) &&
        isCode(f["form_id"]) &&
        Array.isArray(field) &&
        field.length >= 1 &&
        field.length <= HELP_PROPOSAL_FIELD_LIMIT &&
        field.every(isProposedField));
}
function isMode(f) {
    if (!exactKeys(f, ["type", "mode", "cause"], ["display_name"]))
        return false;
    if ("display_name" in f && !isText(f["display_name"], 1, HELP_DISPLAY_NAME_CHAR_LIMIT))
        return false;
    if (f["mode"] === "operator") {
        return isOneOf(f["cause"], ["takeover", "superseded"]) && "display_name" in f;
    }
    return f["mode"] === "assistant" && isOneOf(f["cause"], ["hand_back", "lapsed"]);
}
const ACCOUNT_ACTION_TARGET_KIND = ["voice_agent", "campaign", "gap", "conversation_flag"];
function isAccountActionChange(value) {
    const f = fieldsOf(value);
    return (f !== null &&
        exactKeys(f, ["field", "before", "after"]) &&
        isOneOf(f["field"], HELP_ACCOUNT_ACTION_FIELD) &&
        (f["before"] === null || isText(f["before"], 0, 1_000)) &&
        isText(f["after"], 1, 1_000));
}
/** A card the registry would build: its action's operation, method and a path under `/v1/`, a JSON body or none. */
function isAccountAction(f) {
    if (!exactKeys(f, ["type", "help_action_id", "action", "target", "change", "confirmation", "request", "expires_at"]))
        return false;
    if (!isUuid(f["help_action_id"]) || !isHelpAccountActionCode(f["action"]) || !isInstant(f["expires_at"]))
        return false;
    if (!isOneOf(f["confirmation"], ["click", "step_up"]))
        return false;
    const target = fieldsOf(f["target"]);
    if (target === null || !exactKeys(target, ["kind", "id", "label"]))
        return false;
    if (!isOneOf(target["kind"], ACCOUNT_ACTION_TARGET_KIND) || !isUuid(target["id"]) || !isText(target["label"], 1, 500))
        return false;
    const change = f["change"];
    if (!Array.isArray(change) || change.length < 1 || change.length > 4 || !change.every(isAccountActionChange))
        return false;
    const request = fieldsOf(f["request"]);
    if (request === null || !exactKeys(request, ["operation", "method", "path", "body"]))
        return false;
    const entry = HELP_ACCOUNT_ACTION[f["action"]];
    if (request["operation"] !== entry.operationId || request["method"] !== entry.method)
        return false;
    if (typeof request["path"] !== "string" || !/^\/v1\/[a-z0-9/_-]+$/u.test(request["path"]))
        return false;
    const body = request["body"];
    if (body === null)
        return true;
    if (typeof body !== "string" || body.length > 4_096)
        return false;
    try {
        const parsed = JSON.parse(body);
        return fieldsOf(parsed) !== null;
    }
    catch {
        return false;
    }
}
const SERVER_GUARD = {
    ready: isReady,
    reply_start: (f) => exactKeys(f, ["type", "turn_index"]) && isIndex(f["turn_index"]),
    reply_text: (f) => exactKeys(f, ["type", "turn_index", "delta"]) && isIndex(f["turn_index"]) && typeof f["delta"] === "string",
    reply_end: isReplyEnd,
    proposal: isProposal,
    offer: (f) => exactKeys(f, ["type", "help_request_kind", "summary"]) &&
        isHelpRequestKind(f["help_request_kind"]) &&
        isText(f["summary"], 1, HELP_OFFER_SUMMARY_CHAR_LIMIT),
    waiting: (f) => exactKeys(f, ["type", "since"]) && isInstant(f["since"]),
    no_person: (f) => exactKeys(f, ["type", "reason"]) && isOneOf(f["reason"], HELP_NO_PERSON_REASON),
    mode: isMode,
    operator_line: (f) => exactKeys(f, ["type", "help_operator_line_id", "turn_index", "text"]) &&
        isUuid(f["help_operator_line_id"]) &&
        isIndex(f["turn_index"]) &&
        isText(f["text"], 1, HELP_OPERATOR_LINE_CHAR_LIMIT),
    refused: (f) => exactKeys(f, ["type", "reason"], ["retry_at"]) &&
        isOneOf(f["reason"], HELP_REFUSED_REASON) &&
        (!("retry_at" in f) || isInstant(f["retry_at"])),
    scrubbed: (f) => exactKeys(f, ["type"]),
    page_reading_offer: (f) => exactKeys(f, ["type"]),
    closed: (f) => exactKeys(f, ["type", "reason"]) && isOneOf(f["reason"], HELP_CLOSED_REASON),
    account_action: isAccountAction,
    account_read_note: (f) => exactKeys(f, ["type"]),
};
/** Whether a parsed value is exactly one of the server frames. */
export function isHelpServerFrame(value) {
    const f = fieldsOf(value);
    if (f === null || !isOneOf(f["type"], HELP_SERVER_FRAME_TYPE))
        return false;
    return SERVER_GUARD[f["type"]](f);
}
/** Reads one text frame from the help host; `null` for anything that is not exactly a server frame. Never throws. */
export function readHelpServerFrame(text) {
    let value;
    try {
        value = JSON.parse(text);
    }
    catch {
        return null;
    }
    return isHelpServerFrame(value) ? value : null;
}
//# sourceMappingURL=protocol.js.map